/**
 * Notification Context
 *
 * React context for managing notification messages (success/error).
 * Provides centralized notification state for product page components.
 *
 * Pattern follows ui-components module contexts:
 * - createContext with null default
 * - Provider with useMemo for context value
 * - useCallback for stable function references
 * - Hook with null check and error throw
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

/**
 * Notification interface
 */
export interface Notification {
  id: string;
  message: string;
  type: 'success' | 'error';
  timestamp: number;
}

/**
 * Notification context value interface
 */
interface NotificationContextValue {
  notifications: Notification[];
  showNotification: (message: string, type: 'success' | 'error') => void;
}

/**
 * Notification Provider props interface
 */
export interface NotificationProviderProps {
  children: React.ReactNode;
}

/**
 * Notification Context
 */
const NotificationContext = createContext<NotificationContextValue | null>(null);

/**
 * Notification Provider Component
 *
 * Manages notification state and provides API to all child components.
 */
export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback((message: string, type: 'success' | 'error') => {
    const notification: Notification = {
      id: `${Date.now()}-${Math.random()}`,
      message,
      type,
      timestamp: Date.now()
    };
    setNotifications(prev => [...prev, notification]);
  }, []);

  const contextValue = useMemo<NotificationContextValue>(() => ({
    notifications,
    showNotification
  }), [notifications, showNotification]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook to use notification context
 *
 * @throws Error when used outside NotificationProvider
 */
export function useNotification(): NotificationContextValue {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }

  return context;
}
